import { AlertTriangle, Mail, ShieldCheck, SlidersHorizontal, Trophy, WalletCards } from "lucide-react";

export type SettingImpact = "critical" | "warning" | "normal";
export type SystemSettingsCategoryId = "financial" | "security" | "mail" | "rewards";

export interface SettingCopy {
  label: string;
  helper: string;
  unit?: string;
  impact: SettingImpact;
  options?: { value: string; label: string; detail: string }[];
}

export const SYSTEM_SETTINGS_CATEGORIES: Record<
  SystemSettingsCategoryId,
  {
    title: string;
    description: string;
    modules: string[];
    kpiLabel: string;
    icon: typeof WalletCards;
    loadingLabel: string;
  }
> = {
  financial: {
    title: "Finances",
    description:
      "Paramètres qui modifient les documents, crédits, créances, pénalités et épargne voyage.",
    modules: ["M2", "M3", "M6", "M11"],
    kpiLabel: "Règles financières",
    icon: WalletCards,
    loadingLabel: "Chargement des règles financières...",
  },
  security: {
    title: "Sécurité",
    description: "Paramètres de connexion, validité OTP et verrouillage temporaire des comptes.",
    modules: ["M1"],
    kpiLabel: "Règles de sécurité",
    icon: ShieldCheck,
    loadingLabel: "Chargement des règles de sécurité...",
  },
  mail: {
    title: "E-mails",
    description:
      "Interrupteurs d'envoi, identité expéditeur, rappels automatiques et diagnostics SMTP.",
    modules: ["MAIL"],
    kpiLabel: "Réglages e-mail",
    icon: Mail,
    loadingLabel: "Chargement des réglages e-mail...",
  },
  rewards: {
    title: "Récompenses",
    description:
      "Règles de prévisualisation des récompenses clients, référents et comparatif interne employés.",
    modules: ["REWARDS"],
    kpiLabel: "Règles de récompense",
    icon: Trophy,
    loadingLabel: "Chargement des règles de récompense...",
  },
};

export const SETTING_COPY: Record<string, SettingCopy> = {
  penalty_amount_xaf: {
    label: "Montant de pénalité hebdomadaire",
    helper: "Ajouté à chaque échéance en retard pour chaque semaine complète dépassée.",
    unit: "XAF",
    impact: "critical",
  },
  penalty_grace_days: {
    label: "Délai avant pénalité",
    helper: "Nombre de jours accordés après une échéance manquée avant le calcul des pénalités.",
    unit: "jours",
    impact: "critical",
  },
  credit_decision_period_days: {
    label: "Période de décision crédit",
    helper: "Durée pendant laquelle le client peut choisir quoi faire d'un lot de crédit.",
    unit: "jours",
    impact: "warning",
  },
  credit_underfee_policy: {
    label: "Crédit inférieur aux frais d'inscription",
    helper: "Décide du traitement des petits crédits expirés avant conversion vers l'épargne.",
    impact: "critical",
    options: [
      {
        value: "HOLD_AND_NOTIFY",
        label: "Mettre en attente et notifier",
        detail: "Le crédit reste bloqué pour revue manuelle.",
      },
      {
        value: "CONVERT_ANYWAY",
        label: "Convertir malgré tout",
        detail: "Le crédit est converti même s'il ne couvre pas les frais.",
      },
    ],
  },
  epargne_inscription_fee: {
    label: "Frais d'inscription épargne",
    helper: "Montant prélevé lors de l'inscription à l'épargne voyage.",
    unit: "XAF",
    impact: "critical",
  },
  default_currency: {
    label: "Devise par défaut",
    helper: "Devise proposée dans les documents et calculs financiers.",
    impact: "warning",
  },
  default_due_date_offset_days: {
    label: "Échéance par défaut des factures",
    helper: "Nombre de jours ajouté à la date d'émission en mode paiement complet.",
    unit: "jours",
    impact: "warning",
  },
  otp_expiration_minutes: {
    label: "Validité du code OTP",
    helper: "Durée pendant laquelle un code de vérification reste utilisable.",
    unit: "minutes",
    impact: "normal",
  },
  lockout_max_attempts: {
    label: "Tentatives de connexion autorisées",
    helper: "Nombre d'échecs de connexion avant verrouillage temporaire du compte.",
    unit: "tentatives",
    impact: "warning",
  },
  lockout_duration_minutes: {
    label: "Durée du verrouillage",
    helper: "Temps d'attente imposé après dépassement du nombre de tentatives autorisées.",
    unit: "minutes",
    impact: "warning",
  },
  mail_enabled: {
    label: "Envoi d'e-mails activé",
    helper: "Interrupteur global. Désactive tous les e-mails sortants sans modifier la configuration SMTP.",
    impact: "critical",
  },
  mail_automatic_reminders_enabled: {
    label: "Rappels automatiques",
    helper:
      "Autorise les futurs envois automatiques pour les échéances proches, les paiements en retard et les relances.",
    impact: "critical",
  },
  mail_document_auto_send_enabled: {
    label: "Envoi automatique des documents",
    helper:
      "Autorise l'envoi automatique par e-mail des proformas, factures et bons de livraison à la génération.",
    impact: "critical",
  },
  mail_sender_name: {
    label: "Nom d'expéditeur",
    helper: "Nom affiché dans les e-mails générés par l'application.",
    impact: "warning",
  },
  mail_sender_email: {
    label: "Adresse e-mail d'expéditeur",
    helper:
      "Adresse utilisée comme expéditeur des e-mails sortants. Si laissée vide, la configuration serveur (SMTP) est utilisée.",
    impact: "critical",
  },
  mail_owner_reminder_cc_enabled: {
    label: "Copie interne des rappels",
    helper:
      "Envoie aussi les rappels de factures en retard aux administrateurs actifs pour suivi interne.",
    impact: "warning",
  },
  rewards_enabled: {
    label: "Prévisualisation des récompenses",
    helper:
      "Active le suivi informatif des récompenses. Aucun paiement, crédit ou mouvement comptable n'est créé.",
    impact: "warning",
  },
  rewards_client_enabled: {
    label: "Récompenses clients",
    helper: "Inclut les clients dans le classement et le calcul estimatif des récompenses.",
    impact: "warning",
  },
  rewards_client_threshold_gain: {
    label: "Seuil client",
    helper: "Gain minimum à atteindre avant qu'un client soit considéré comme éligible.",
    unit: "XAF",
    impact: "warning",
  },
  rewards_client_rate_bps: {
    label: "Taux client",
    helper: "Taux appliqué au gain client pour l'estimation. 100 points = 1 %.",
    unit: "points",
    impact: "warning",
  },
  rewards_client_fixed_amount: {
    label: "Forfait client",
    helper: "Montant fixe ajouté à l'estimation lorsqu'un client est éligible.",
    unit: "XAF",
    impact: "warning",
  },
  rewards_referrer_enabled: {
    label: "Récompenses référents",
    helper: "Inclut les référents et apporteurs dans le classement et le calcul estimatif.",
    impact: "warning",
  },
  rewards_referrer_threshold_gain: {
    label: "Seuil référent",
    helper: "Gain minimum à atteindre avant qu'un référent soit considéré comme éligible.",
    unit: "XAF",
    impact: "warning",
  },
  rewards_referrer_rate_bps: {
    label: "Taux référent",
    helper: "Taux appliqué au gain référent pour l'estimation. 100 points = 1 %.",
    unit: "points",
    impact: "warning",
  },
  rewards_referrer_fixed_amount: {
    label: "Forfait référent",
    helper: "Montant fixe ajouté à l'estimation lorsqu'un référent est éligible.",
    unit: "XAF",
    impact: "warning",
  },
  rewards_employee_enabled: {
    label: "Suivi interne employés",
    helper: "Inclut les employés dans la comparaison interne des avantages ou primes possibles.",
    impact: "warning",
  },
  rewards_employee_threshold_gain: {
    label: "Seuil employé",
    helper: "Gain minimum à atteindre avant qu'un employé soit considéré comme éligible dans la simulation.",
    unit: "XAF",
    impact: "warning",
  },
  rewards_employee_rate_bps: {
    label: "Taux employé",
    helper: "Taux appliqué au gain employé pour l'estimation interne. 100 points = 1 %.",
    unit: "points",
    impact: "warning",
  },
  rewards_employee_fixed_amount: {
    label: "Forfait employé",
    helper: "Montant fixe ajouté à l'estimation lorsqu'un employé est éligible.",
    unit: "XAF",
    impact: "warning",
  },
};

export const IMPACT_STYLES = {
  critical: {
    label: "Impact élevé",
    className: "border-danger-border bg-danger-bg text-danger-text",
    cardClassName:
      "border-danger-border bg-[repeating-linear-gradient(135deg,var(--danger-bg)_0,var(--danger-bg)_10px,var(--card)_10px,var(--card)_22px)]",
    ruleClassName: "border-danger-border",
    icon: AlertTriangle,
  },
  warning: {
    label: "Impact moyen",
    className: "border-warning-border bg-warning-bg text-warning-text",
    cardClassName:
      "border-warning-border bg-[repeating-linear-gradient(135deg,var(--warning-bg)_0,var(--warning-bg)_10px,var(--card)_10px,var(--card)_22px)]",
    ruleClassName: "border-warning-border",
    icon: SlidersHorizontal,
  },
  normal: {
    label: "Impact faible",
    className: "border-success-border bg-success-bg text-success-text",
    cardClassName:
      "border-success-border bg-[repeating-linear-gradient(135deg,var(--success-bg)_0,var(--success-bg)_10px,var(--card)_10px,var(--card)_22px)]",
    ruleClassName: "border-success-border",
    icon: ShieldCheck,
  },
};
